import React, { useState, useMemo, useCallback } from "react";
import BdrModal from "../../../../components/BdrModal/BdrModal";
import BdrSelectPopup from "../../../../components/BdrInput/BdrSelectPopup";
import BdrInput from "../../../../components/BdrInput/BdrInput";
import BdrButton from "../../../../components/BdrButton/BdrButton";
import { useLazyQuery, useQuery, useMutation } from "@apollo/client";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { CircularProgress, Switch } from "@mui/material";
import {
  GET_JOB_FORM_BY_INVOICE_ID,
  GET_CLIENT_INVOICE_BY_ID,
  GET_JOB_OPTIONS_LIST,
  GET_ALL_REFERRED_BY_OPTIONS,
  GET_CLIENT_REFERRED_BY,
  GET_ACTIVE_USERS_FOR_REFERRAL,
  GET_ALL_HARDWARE_OPERATION_OPTIONS,
} from "../../../../utils/GraphQL/queries";
import {
  priceStrFormatter,
  removePriceStrFormatter,
} from "../../../../utils/formatters/priceStrFormatter";
import {
  CREATE_OR_UPDATE_JOB_FORM,
  UPDATE_JOB_FORM_STATUS,
  UPDATE_CLIENT_REFERRED_BY,
  DEV_CLEAR_CLIENT_REFERRAL,
} from "../../../../utils/GraphQL/mutations";
import { useSnackbar } from "../../../../utils/GlobalSnackbar/GlobalSnackbarContext";
import { useConfirm } from "../../../../utils/GlobalConfirm/GlobalConfirmContext";
import Auth from "../../../../utils/auth";
import { hasRole } from "../../../../utils/permissions";
import {
  validateJobFormReview,
  validateJobFormReviewForApproval,
} from "../../../../utils/formValidators/jobFormReviewValidator";
import { useSSEListener } from "../../../../utils/SSE/SSEContext";
import CustomerNotesHelperButton from "../CustomerNotesHelperButton/CustomerNotesHelperButton";
import { baImageUrl } from "../../../../utils/baImageUrl";
import "./JobFormReviewModal.css";

// Image-migration read-swap: resolve a populated photo to its DIRECT S3/CloudFront
// URL, falling back to the /ba-image redirect (which re-signs / serves base64 for
// any not-yet-migrated photo). Full-size for the lightbox; thumb for grids.
const photoFull = (p) => p?.s3ImageUrl ?? baImageUrl(p?._id);
const photoThumb = (p) =>
  p?.s3ThumbUrl ?? baImageUrl(p?._id, { variant: "thumb" });

const formatDate = (ms) => {
  if (!ms) return "--";
  const d = new Date(Number(ms));
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const statusLabel = (s) => {
  switch (s) {
    case "draft": return "Draft";
    case "in_progress": return "In Progress";
    case "pending":
    case "submitted_for_review": return "Pending";
    case "approved":
    case "completed": return "Approved";
    case "not_started": return "Not Started";
    default: return s || "Draft";
  }
};

const statusCssClass = (s) => {
  switch (s) {
    case "pending":
    case "submitted_for_review": return "submitted";
    case "approved":
    case "completed": return "completed";
    case "in_progress": return "in_progress";
    case "draft": return "draft";
    default: return s || "draft";
  }
};

/**
 * Detect the STALE_VERSION GraphQL error thrown by the server's save-side
 * OCC (Phase 1). When the form has advanced past the client's
 * `expectedVersion`, the resolver throws an error tagged with
 * `extensions.code === "STALE_VERSION"` and `extensions.currentVersion`
 * so the client can resync before retrying. Match against the Apollo error
 * shape (graphQLErrors carry the extensions) AND a message-string fallback
 * for any envelope that flattens extensions out.
 */
const isStaleVersionError = (err) => {
  if (!err) return false;
  const gqlErrs = err.graphQLErrors || err.networkError?.result?.errors || [];
  for (const gqlErr of gqlErrs) {
    if (gqlErr?.extensions?.code === "STALE_VERSION") return true;
  }
  return /STALE_VERSION|Form was updated by another user/i.test(err.message || "");
};

const getStaleCurrentVersion = (err) => {
  const gqlErrs = err?.graphQLErrors || err?.networkError?.result?.errors || [];
  for (const gqlErr of gqlErrs) {
    if (gqlErr?.extensions?.code === "STALE_VERSION") {
      return gqlErr.extensions.currentVersion ?? null;
    }
  }
  return null;
};

const JobFormReviewModal = ({ open, onClose, invoiceId, onStatusChanged }) => {
  const { handleSnack } = useSnackbar();
  const { confirm } = useConfirm();

  // Developer-role check — gates the dev-clear button below the referredByUser
  // dropdown. Computed once on mount; we don't need to react to role flips
  // mid-session.
  const isDeveloper = (() => {
    try {
      const { data: profile } = Auth.getProfile();
      return hasRole(profile?.permission, "developer");
    } catch {
      return false;
    }
  })();

  const [editState, setEditState] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [modalImg, setModalImg] = useState(null);
  const [galleryMode, setGalleryMode] = useState({}); // { [groupId]: bool }
  // Tip display parity with JobFormReviewPage (2026-07-10): the calculated
  // tip needs the linked invoice's total + line items, which the job-form
  // query doesn't carry — fetched separately on open.
  const [invoiceData, setInvoiceData] = useState(null);

  // Live-updates Phase 2: per-modal-instance `lastFetchedVersion`. Tracked
  // in a ref (not state) so save-conflict retries don't trigger an extra
  // render. Updated by every fetchForm onCompleted and by every successful
  // saveForm / updateStatus response that returns `version`.
  const lastFetchedVersionRef = React.useRef(null);

  // Live-updates Phase 4: active viewers strip. Initialized from
  // `form.activeViewers` in fetchForm.onCompleted, then live-updated by
  // the `job_form_presence_update` SSE listener so other reviewers
  // appear/disappear within 1-3s of join/leave instead of waiting for
  // the next 30s heartbeat tick.
  const [activeViewers, setActiveViewers] = useState([]);

  // --- Queries ---
  const [fetchForm, { loading: formLoading }] = useLazyQuery(
    GET_JOB_FORM_BY_INVOICE_ID,
    {
      fetchPolicy: "network-only",
      onCompleted: (data) => {
        const form = data?.getJobFormByInvoiceId;
        if (form) {
          // Live-updates Phase 2: snapshot version for the next save's
          // expectedVersion. null when server is pre-Phase-1 (legacy
          // path, OCC bypassed).
          lastFetchedVersionRef.current = form.version ?? null;
          // Live-updates Phase 4: seed active viewers from initial
          // fetch. SSE updates replace this in-place — no refetch
          // needed for membership changes.
          setActiveViewers(form.activeViewers || []);
          setEditState({
            stainOptionId: form.stainOptionId?._id || "",
            stainIsOther: form.stainIsOther || false,
            customStainText: form.customStainText || "",
            hardwareOptionId: form.hardwareOptionId?._id || "",
            hardwareIsOther: form.hardwareIsOther || false,
            customHardwareText: form.customHardwareText || "",
            // Post job form omnibus Phase 7 (2026-05-14)
            dryDoorOptionId: form.dryDoorOptionId?._id || "",
            dryDoorIsOther: form.dryDoorIsOther || false,
            customDryDoorText: form.customDryDoorText || "",
            thresholdIsAdjustable: form.thresholdIsAdjustable || false,
            adjustedThreshold: form.adjustedThreshold ?? null,
            replacedDoorSweep: form.replacedDoorSweep ?? null,
            // 2026-07-22 parity with JobFormReviewPage: weather strip /
            // foam wedges / new hardware / handle-deadbolt operations were
            // fetched but dropped here, so the modal couldn't correct them.
            changedWeatherStrip: form.changedWeatherStrip || false,
            foamWedges: form.foamWedges || "",
            installedNewHardware: form.installedNewHardware || false,
            // Defensive string-id extraction — same rationale as the page:
            // an object slipping into <select value=> matches no option.
            handleOperationId:
              typeof form.handleOperationId === "string"
                ? form.handleOperationId
                : form.handleOperationId?._id || "",
            deadboltOperationId:
              typeof form.deadboltOperationId === "string"
                ? form.deadboltOperationId
                : form.deadboltOperationId?._id || "",
            customerSatisfaction: form.customerSatisfaction || "",
            amountReceived: form.amountReceived || "",
            paymentMethod: form.paymentMethod || "",
            notes: form.notes || "",
            // Push 1 build 146: referredBy state seeded from
            // getClientReferredBy below (separate query because the
            // form's getJobFormByInvoiceId doesn't carry it).
            referredBySource: "",
            referredBySourceOptionId: "",
            // 2026-04-29: explicit "is Other" flag matching the
            // stainIsOther / hardwareIsOther pattern. Drives the dropdown
            // value AND the custom-input visibility — the prior
            // derivation `source && !optionId` failed on initial Other
            // selection (both fields empty → input never showed).
            referredByIsOther: false,
            referredByOriginalSource: "",
            referredByOriginalSourceOptionId: "",
            // Phase 5: referredByUser dropdown. See REFERRAL_SYSTEM_PLAN.md.
            referredByUserId: "",
            referredByOriginalUserId: "",
            referredByUserIdLockedByServer: false,
            _raw: form,
          });
          setValidationErrors([]);
          // Push 1 build 146: kick off the source fetch as soon as
          // we know the clientId. The result lands in onCompleted
          // below and merges into editState in-place.
          if (form.clientId) {
            fetchClientReferredBy({ variables: { clientId: form.clientId } });
          }
        }
      },
    }
  );

  const [fetchInvoice] = useLazyQuery(GET_CLIENT_INVOICE_BY_ID, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      setInvoiceData(data?.getClientInvoiceById || null);
    },
  });

  const { data: stainData } = useQuery(GET_JOB_OPTIONS_LIST, {
    variables: { listType: "stain" },
  });
  const { data: dryDoorData } = useQuery(GET_JOB_OPTIONS_LIST, {
    variables: { listType: "dryDoor" },
  });

  const { data: hwData } = useQuery(GET_JOB_OPTIONS_LIST, {
    variables: { listType: "hardwareBrand" },
  });

  // 2026-07-22 parity: handle/deadbolt operation dropdowns (previously
  // JobFormReviewPage-only). network-only for the same select-hydration
  // reason documented there.
  const { data: hwOpData } = useQuery(GET_ALL_HARDWARE_OPERATION_OPTIONS, {
    fetchPolicy: "network-only",
  });

  // Push 1 build 146: referredBy options + client's current referredBy.
  const { data: referredByData } = useQuery(GET_ALL_REFERRED_BY_OPTIONS);
  const referredByOptions = useMemo(
    () => referredByData?.getAllReferredByOptions || [],
    [referredByData]
  );
  const [fetchClientReferredBy] = useLazyQuery(GET_CLIENT_REFERRED_BY, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      const rb = data?.getClientReferredBy;
      if (!rb) return;
      setEditState((prev) =>
        prev
          ? {
              ...prev,
              referredBySource: rb.source || "",
              referredBySourceOptionId: rb.sourceOptionId || "",
              // Seed isOther for existing data: a non-empty source with
              // no sourceOptionId is an Other entry. This handles forms
              // saved before the explicit flag existed.
              referredByIsOther: Boolean(
                rb.source && !rb.sourceOptionId
              ),
              referredByOriginalSource: rb.source || "",
              referredByOriginalSourceOptionId: rb.sourceOptionId || "",
              // Phase 5: seed userId. Locked once set per Q1.
              referredByUserId: rb.userId || "",
              referredByOriginalUserId: rb.userId || "",
              referredByUserIdLockedByServer: Boolean(rb.userId),
            }
          : prev
      );
    },
  });

  // Phase 5: active users for the referredByUser dropdown.
  const { data: activeUsersData } = useQuery(GET_ACTIVE_USERS_FOR_REFERRAL, {
    fetchPolicy: "cache-first",
  });
  const activeUsersForReferral = activeUsersData?.getActiveUsersForReferral || [];

  const [saveForm, { loading: saving }] = useMutation(CREATE_OR_UPDATE_JOB_FORM);
  const [updateStatus, { loading: statusUpdating }] = useMutation(UPDATE_JOB_FORM_STATUS);
  const [updateClientReferredBy, { loading: clientUpdating }] = useMutation(
    UPDATE_CLIENT_REFERRED_BY
  );
  const [devClearClientReferral, { loading: devClearing }] = useMutation(
    DEV_CLEAR_CLIENT_REFERRAL
  );

  // Dev-only escape hatch — full-cascade clear of an accidental refinisher
  // referral assignment. Confirm modal + mutation + local state reset. The
  // SSE broadcast on success refetches lists for other open admin sessions;
  // this modal doesn't auto-listen for job_form_update, so the originator's
  // state is updated inline here.
  const handleDevClearReferral = useCallback(async () => {
    const rawForm = editState?._raw;
    if (!rawForm || !rawForm.clientId || !rawForm._id) {
      handleSnack("Cannot clear: job form not fully loaded", 4000);
      return;
    }
    const ok = await confirm({
      title: "Clear refinisher user assignment (dev)?",
      message:
        "Clears the refinisher user credit for this client and deletes the " +
        "linked referral payout. The customer's referral source category " +
        "(e.g., \"Refinisher Referral\") is preserved — only the per-job " +
        "user assignment is undone. After this, the dropdown unlocks so the " +
        "correct refinisher can be picked. Cannot be undone.",
      confirmLabel: "Yes, clear user",
      cancelLabel: "Cancel",
      destructive: true,
    });
    if (!ok) return;
    try {
      await devClearClientReferral({
        variables: {
          clientId: rawForm.clientId,
          jobFormId: rawForm._id,
        },
      });
      setEditState((prev) =>
        prev
          ? {
              ...prev,
              // Source/sourceOptionId intentionally NOT reset — server
              // preserves them. Only the user-credit slot clears.
              referredByUserId: "",
              referredByOriginalUserId: "",
              referredByUserIdLockedByServer: false,
            }
          : prev
      );
      handleSnack("Refinisher user assignment cleared", 3000);
    } catch (err) {
      handleSnack(
        `Clear failed: ${err.message || "Unknown error"}`,
        5000
      );
    }
  }, [editState, confirm, devClearClientReferral, handleSnack]);

  // Push 1 build 146: SSE listener — when payment completes via the
  // payment portal (saveTransaction) for the invoice currently open
  // in this modal, refetch the form so the payment section updates +
  // the Call-For-Payment approval gate clears automatically. Listener
  // is no-op for any other open invoice.
  const handlePaymentCompleted = useCallback(
    (event) => {
      if (!open || !invoiceId) return;
      if (event?.invoiceId && String(event.invoiceId) === String(invoiceId)) {
        fetchForm({ variables: { invoiceId } });
        handleSnack(
          `Payment completed (#${event.invoiceNum || ""}) — form refreshed`,
          3000
        );
      }
    },
    [open, invoiceId, fetchForm, handleSnack]
  );
  useSSEListener("job_form_payment_completed", handlePaymentCompleted);

  // Live-updates Phase 4: presence-delta SSE listener. Updates the
  // active-viewers strip without a form refetch — server emits this
  // event from heartbeatFormPresence/leaveFormPresence only when
  // membership actually changes, so the SSE stream stays quiet during
  // steady-state heartbeating.
  const handlePresenceUpdate = useCallback(
    (event) => {
      if (!open || !invoiceId) return;
      if (!event?.invoiceId || String(event.invoiceId) !== String(invoiceId)) {
        return;
      }
      if (Array.isArray(event.activeViewers)) {
        setActiveViewers(event.activeViewers);
      }
    },
    [open, invoiceId]
  );
  useSSEListener("job_form_presence_update", handlePresenceUpdate);

  // Fetch form when modal opens
  const prevInvoiceRef = React.useRef(null);
  React.useEffect(() => {
    if (open && invoiceId && invoiceId !== prevInvoiceRef.current) {
      prevInvoiceRef.current = invoiceId;
      setEditState(null);
      setValidationErrors([]);
      setGalleryMode({});
      setInvoiceData(null);
      fetchForm({ variables: { invoiceId } });
      fetchInvoice({ variables: { invoiceId } });
    }
    if (!open) {
      prevInvoiceRef.current = null;
    }
  }, [open, invoiceId, fetchForm, fetchInvoice]);

  const stainOptions = useMemo(() => stainData?.getJobOptionsList || [], [stainData]);
  const hardwareOptions = useMemo(() => hwData?.getJobOptionsList || [], [hwData]);
  const dryDoorOptions = useMemo(() => dryDoorData?.getJobOptionsList || [], [dryDoorData]);
  const form = editState?._raw;
  const isMutating = saving || statusUpdating || clientUpdating;
  // Push 1 build 146: derived flag for the Call For Payment banner +
  // approval gate. Trust the live editState so the UI updates the
  // moment the office user changes paymentMethod.
  const isCallForPayment =
    (editState?.paymentMethod || "") === "Call For Payment";

  // --- Handlers ---
  const updateField = useCallback((key, value) => {
    setEditState((prev) => (prev ? { ...prev, [key]: value } : prev));
  }, []);

  const handleStainChange = (e) => {
    const val = e.target.value;
    if (val === "__other__") {
      updateField("stainOptionId", "");
      updateField("stainIsOther", true);
    } else {
      updateField("stainOptionId", val);
      updateField("stainIsOther", false);
      updateField("customStainText", "");
    }
  };

  const handleDryDoorChange = (e) => {
    const val = e.target.value;
    if (val === "__other__") {
      updateField("dryDoorOptionId", "");
      updateField("dryDoorIsOther", true);
    } else {
      updateField("dryDoorOptionId", val);
      updateField("dryDoorIsOther", false);
      updateField("customDryDoorText", "");
    }
  };

  const handleHardwareChange = (e) => {
    const val = e.target.value;
    if (val === "__other__") {
      updateField("hardwareOptionId", "");
      updateField("hardwareIsOther", true);
    } else {
      updateField("hardwareOptionId", val);
      updateField("hardwareIsOther", false);
      updateField("customHardwareText", "");
    }
  };

  // Push 1 build 146: detects an unsaved source change so handleSave
  // and handleApprove can push it to the Client doc before continuing.
  // "Save-on-submit" semantics — closing the modal without clicking
  // Save or Approve does NOT mutate the Client.
  const sourceChanged = useCallback(() => {
    if (!editState) return false;
    return (
      (editState.referredBySource || "") !==
        (editState.referredByOriginalSource || "") ||
      (editState.referredBySourceOptionId || "") !==
        (editState.referredByOriginalSourceOptionId || "") ||
      (editState.referredByUserId || "") !==
        (editState.referredByOriginalUserId || "")
    );
  }, [editState]);

  const persistSourceIfChanged = useCallback(async () => {
    if (!editState || !form?.clientId) return;
    if (!sourceChanged()) return;

    // Source: empty-only backfill (existing behavior). userId (Phase 5):
    // always attempt write when changed; server's applyReferredByGuards
    // enforces the once-set Q1 rule + setAtMs/setByUserId stamping.
    const sourceWasEmpty = !(editState.referredByOriginalSource || "").trim();
    const sourceChangedNow =
      (editState.referredBySource || "") !==
        (editState.referredByOriginalSource || "") ||
      (editState.referredBySourceOptionId || "") !==
        (editState.referredByOriginalSourceOptionId || "");
    const userIdChangedNow =
      (editState.referredByUserId || "") !==
      (editState.referredByOriginalUserId || "");

    const writeSource = sourceChangedNow && sourceWasEmpty;
    const writeUserId = userIdChangedNow;

    if (!writeSource && !writeUserId) {
      // No mutation; just re-baseline.
      setEditState((prev) =>
        prev
          ? {
              ...prev,
              referredByOriginalSource: prev.referredBySource || "",
              referredByOriginalSourceOptionId:
                prev.referredBySourceOptionId || "",
              referredByOriginalUserId: prev.referredByUserId || "",
            }
          : prev
      );
      return;
    }

    const referredByPayload = {};
    if (writeSource) {
      referredByPayload.source = editState.referredBySource || null;
      referredByPayload.sourceOptionId = editState.referredBySourceOptionId || null;
    }
    if (writeUserId) {
      referredByPayload.userId = editState.referredByUserId || null;
    }

    await updateClientReferredBy({
      variables: { clientId: form.clientId, referredBy: referredByPayload },
    });

    setEditState((prev) =>
      prev
        ? {
            ...prev,
            referredByOriginalSource: prev.referredBySource || "",
            referredByOriginalSourceOptionId: prev.referredBySourceOptionId || "",
            referredByOriginalUserId: prev.referredByUserId || "",
            referredByUserIdLockedByServer: Boolean(prev.referredByUserId),
          }
        : prev
    );
  }, [editState, form, sourceChanged, updateClientReferredBy]);

  const handleSave = async () => {
    if (!editState || !form) return;
    const payload = {
      stainOptionId: editState.stainIsOther ? null : editState.stainOptionId || null,
      stainIsOther: editState.stainIsOther,
      customStainText: editState.customStainText,
      hardwareOptionId: editState.hardwareIsOther ? null : editState.hardwareOptionId || null,
      dryDoorOptionId: editState.dryDoorIsOther ? null : editState.dryDoorOptionId || null,
      dryDoorIsOther: editState.dryDoorIsOther,
      customDryDoorText: editState.customDryDoorText,
      thresholdIsAdjustable: editState.thresholdIsAdjustable,
      hardwareIsOther: editState.hardwareIsOther,
      customHardwareText: editState.customHardwareText,
      adjustedThreshold: editState.adjustedThreshold,
      replacedDoorSweep: editState.replacedDoorSweep,
      // 2026-07-22 parity: these were omitted from the modal's save, so
      // office corrections here silently didn't persist.
      changedWeatherStrip: editState.changedWeatherStrip,
      foamWedges: (editState.foamWedges || "").trim(),
      installedNewHardware: editState.installedNewHardware,
      handleOperationId: editState.handleOperationId || null,
      deadboltOperationId: editState.deadboltOperationId || null,
      customerSatisfaction: editState.customerSatisfaction || null,
      amountReceived: editState.amountReceived || null,
      paymentMethod: editState.paymentMethod || null,
      notes: editState.notes,
      selectedRefinisherUserIds: form.selectedRefinisherUserIds || [],
      customRefinisherNames: form.customRefinisherNames || [],
      jobType: form?.jobType || null,
    };

    // 2026-05-08 fix: invoiceItems + bagroupIds were previously inlined
    // on `payload`, which is spread into the saveForm `input` below
    // and broke validation server-side ("Field invoiceItems is not
    // defined by type JobFormInput" + bagroupIds rejected as objects
    // with `__typename: BAGroup`). They're only needed by the client
    // validator (Hardware Change Phase 3 photos-required check), so
    // they live on a separate `validatorPayload` that never reaches
    // the mutation input.
    const validatorPayload = {
      ...payload,
      invoiceItems: form?.invoice?.invoiceItems || [],
      bagroupIds: form?.bagroupIds || [],
    };
    const result = validateJobFormReview(validatorPayload);
    if (!result.valid) {
      setValidationErrors(result.errors);
      return;
    }
    setValidationErrors([]);

    try {
      // Push 1 build 146: persist source change first if any. The
      // Client doc is the source of truth the Ledger reads from at
      // approval time, so saving it in the same handler keeps the
      // two writes atomic from the user's perspective.
      await persistSourceIfChanged();
      const saveResult = await saveForm({
        variables: {
          input: {
            jobFormId: form._id,
            invoiceId: form.invoiceId,
            calendarItemId: form.calendarItemId,
            clientId: form.clientId,
            ...payload,
            // Live-updates Phase 2: pass the version we last fetched so
            // the server's atomic OCC catches concurrent edits and
            // returns STALE_VERSION instead of silently overwriting.
            expectedVersion: lastFetchedVersionRef.current,
          },
        },
      });
      // Update version snapshot from the response so an immediate
      // follow-up save (e.g. user clicks Save then Approve) uses the
      // latest baseline.
      const newVersion = saveResult?.data?.createOrUpdateJobForm?.version;
      if (typeof newVersion === "number") {
        lastFetchedVersionRef.current = newVersion;
      }
      handleSnack("Form saved successfully", 3000);
      fetchForm({ variables: { invoiceId } });
    } catch (err) {
      if (isStaleVersionError(err)) {
        // Live-updates Phase 2: another user edited the form while we
        // were composing this save. Refetch + reapply local edits where
        // focus permits. The user's in-progress field values stay in
        // editState; the version snapshot updates via fetchForm's
        // onCompleted, then they can click Save again.
        const cur = getStaleCurrentVersion(err);
        handleSnack(
          `Form was updated by another user${cur != null ? ` (v${cur})` : ""} — refreshed. Review and save again.`,
          5000
        );
        fetchForm({ variables: { invoiceId } });
        return;
      }
      handleSnack("Error saving form: " + (err.message || "Unknown error"), 4000);
    }
  };

  const handleApprove = async () => {
    if (!form?._id || !editState) return;
    // Push 1 build 146: stricter validation gate before approval.
    // Includes everything in the save-time validator PLUS:
    //   - Referral source must be set (from the new Source dropdown)
    //   - paymentMethod must NOT be "Call For Payment"
    // Server-side updateJobFormStatus enforces the same rules as
    // defense-in-depth — UI rejection is just for clearer UX.
    const approvalPayload = {
      stainOptionId: editState.stainIsOther ? null : editState.stainOptionId || null,
      stainIsOther: editState.stainIsOther,
      customStainText: editState.customStainText,
      hardwareOptionId: editState.hardwareIsOther ? null : editState.hardwareOptionId || null,
      dryDoorOptionId: editState.dryDoorIsOther ? null : editState.dryDoorOptionId || null,
      dryDoorIsOther: editState.dryDoorIsOther,
      customDryDoorText: editState.customDryDoorText,
      thresholdIsAdjustable: editState.thresholdIsAdjustable,
      hardwareIsOther: editState.hardwareIsOther,
      customHardwareText: editState.customHardwareText,
      replacedDoorSweep: editState.replacedDoorSweep,
      foamWedges: (editState.foamWedges || "").trim(),
      paymentMethod: editState.paymentMethod || null,
      selectedRefinisherUserIds: form.selectedRefinisherUserIds || [],
      customRefinisherNames: form.customRefinisherNames || [],
      referredBySource: editState.referredBySource || "",
      referredBySourceOptionId: editState.referredBySourceOptionId || "",
      referredByIsOther: Boolean(editState.referredByIsOther),
      jobType: form?.jobType || null,
      invoiceItems: form?.invoice?.invoiceItems || [],
      bagroupIds: form?.bagroupIds || [],
    };
    const approvalResult = validateJobFormReviewForApproval(approvalPayload);
    if (!approvalResult.valid) {
      setValidationErrors(approvalResult.errors);
      return;
    }
    setValidationErrors([]);

    try {
      // Persist source to Client first so the ledger creation downstream
      // picks up the new value. Fail-fast — if this throws, abort
      // approval before server-side ledger creation can run on stale data.
      await persistSourceIfChanged();
      const statusResult = await updateStatus({
        variables: {
          jobFormId: form._id,
          status: "approved",
          // Live-updates Phase 2: OCC on status change so a concurrent
          // refinisher save doesn't get silently lost when admin approves.
          expectedVersion: lastFetchedVersionRef.current,
        },
      });
      const newVersion = statusResult?.data?.updateJobFormStatus?.version;
      if (typeof newVersion === "number") {
        lastFetchedVersionRef.current = newVersion;
      }
      handleSnack("Form approved successfully", 3000);
      onStatusChanged?.();
      onClose();
    } catch (err) {
      if (isStaleVersionError(err)) {
        const cur = getStaleCurrentVersion(err);
        handleSnack(
          `Form was updated by another user${cur != null ? ` (v${cur})` : ""} — refreshed. Review and approve again.`,
          5000
        );
        fetchForm({ variables: { invoiceId } });
        return;
      }
      handleSnack("Error approving form: " + (err.message || "Unknown error"), 4000);
    }
  };

  const handleRequestChanges = async () => {
    if (!form?._id) return;
    try {
      const statusResult = await updateStatus({
        variables: {
          jobFormId: form._id,
          status: "draft",
          expectedVersion: lastFetchedVersionRef.current,
        },
      });
      const newVersion = statusResult?.data?.updateJobFormStatus?.version;
      if (typeof newVersion === "number") {
        lastFetchedVersionRef.current = newVersion;
      }
      handleSnack("Changes requested - form unlocked for refinisher", 3000);
      onStatusChanged?.();
      onClose();
    } catch (err) {
      if (isStaleVersionError(err)) {
        const cur = getStaleCurrentVersion(err);
        handleSnack(
          `Form was updated by another user${cur != null ? ` (v${cur})` : ""} — refreshed. Try again.`,
          5000
        );
        fetchForm({ variables: { invoiceId } });
        return;
      }
      handleSnack("Error requesting changes: " + (err.message || "Unknown error"), 4000);
    }
  };

  const toggleGallery = (groupId) => {
    setGalleryMode((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  return (
    <>
      <BdrModal
        open={open}
        onClose={onClose}
        variant="panel"
        size="lg"
        showHeader={false}
        ariaLabel="Job form review"
        /* raised: opened from the day / week / day-view modals */
        layer="raised"
      >
        {/* Header */}
        <div className="jfrm-header">
          <h2 className="jfrm-title">Job Form Review</h2>
          <IconButton onClick={onClose} sx={{ color: "#fff" }}>
            <CloseIcon />
          </IconButton>
        </div>

        <div className="cal-modal-body">
          {formLoading || !form ? (
            <div className="jfrm-loading">
              <CircularProgress size={32} sx={{ color: "#fff" }} />
            </div>
          ) : (
            <div className="jfrm-content bdr-dark-placeholder">
              {/* Status badge */}
              <div className="jfrm-status-row">
                <span className={`jfr-status-badge ${statusCssClass(form.status)}`}>
                  {statusLabel(form.status)}
                </span>
                {form.dateSubmitted && (
                  <span className="jfrm-submitted-date">
                    Submitted {formatDate(form.dateSubmitted)}
                  </span>
                )}
                {/* Live-updates Phase 4: active viewers strip. SSE-driven —
                    appears within 1-3s of another user opening the same
                    form. Initial dots come from form.activeViewers; SSE
                    delta updates the state in-place. */}
                {activeViewers && activeViewers.length > 0 && (
                  <span
                    className="jfrm-active-viewers"
                    title={`Currently viewing: ${activeViewers
                      .map((v) => v.userName || v.userId)
                      .join(", ")}`}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                      marginLeft: 8,
                    }}
                  >
                    {activeViewers.map((v) => {
                      const initials = (v.userName || v.userId || "?")
                        .split(/\s+/)
                        .map((p) => p.charAt(0))
                        .join("")
                        .slice(0, 2)
                        .toUpperCase();
                      return (
                        <span
                          key={v.userId}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 24,
                            height: 24,
                            borderRadius: "50%",
                            background: v.userColor || "#3a7bd5",
                            color: "#fff",
                            fontSize: 11,
                            fontWeight: 600,
                            border: "1px solid rgba(255,255,255,0.4)",
                          }}
                        >
                          {initials}
                        </span>
                      );
                    })}
                  </span>
                )}
              </div>

              {/* Validation errors */}
              {validationErrors.length > 0 && (
                <div className="jfr-validation-errors">
                  {validationErrors.map((err, i) => (
                    <span key={i} className="jfr-validation-error">{err}</span>
                  ))}
                </div>
              )}

              {/* Job Details */}
              <div className="jfrm-section">
                <div className="jfrm-section-title">Job Details</div>
                <div className="jfrm-fields-grid">
                  {/* Stain */}
                  <div className="jfrm-field">
                    <label className="bdr-field-label">Stain</label>
                    <BdrSelectPopup
                      id="jfrm-stain"
                      className="bdr-input bdr-select-trigger"
                      label="Stain"
                      value={editState.stainIsOther ? "__other__" : editState.stainOptionId}
                      onChange={handleStainChange}
                      placeholder="-- Select --"
                      options={[
                        ...stainOptions.map((opt) => ({
                          value: opt._id,
                          label: opt.title,
                        })),
                        { value: "__other__", label: "Other" },
                      ]}
                    />
                    {editState.stainIsOther && (
                      <input
                        type="text"
                        className="bdr-input"
                        placeholder="Custom stain..."
                        value={editState.customStainText}
                        onChange={(e) => updateField("customStainText", e.target.value)}
                      />
                    )}
                  </div>

                  {/* Hardware */}
                  <div className="jfrm-field">
                    <label className="bdr-field-label">Hardware Brand</label>
                    <BdrSelectPopup
                      id="jfrm-hardware"
                      className="bdr-input bdr-select-trigger"
                      label="Hardware"
                      value={editState.hardwareIsOther ? "__other__" : editState.hardwareOptionId}
                      onChange={handleHardwareChange}
                      placeholder="-- Select --"
                      options={[
                        ...hardwareOptions.map((opt) => ({
                          value: opt._id,
                          label: opt.title,
                        })),
                        { value: "__other__", label: "Other" },
                      ]}
                    />
                    {editState.hardwareIsOther && (
                      <input
                        type="text"
                        className="bdr-input"
                        placeholder="Custom hardware..."
                        value={editState.customHardwareText}
                        onChange={(e) => updateField("customHardwareText", e.target.value)}
                      />
                    )}
                  </div>

                  {/* Dry Door — post job form omnibus Phase 7, 2026-05-14.
                      Hidden on HC. Required on R/M/RM. */}
                  {editState.jobType !== "HC" && (
                    <div className="jfrm-field">
                      <label className="bdr-field-label">Dry Door</label>
                      <BdrSelectPopup
                        id="jfrm-dry-door"
                        className="bdr-input bdr-select-trigger"
                        label="Dry door"
                        value={editState.dryDoorIsOther ? "__other__" : editState.dryDoorOptionId}
                        onChange={handleDryDoorChange}
                        placeholder="-- Select --"
                        options={[
                          ...dryDoorOptions.map((opt) => ({
                            value: opt._id,
                            label: opt.title,
                          })),
                          { value: "__other__", label: "Other" },
                        ]}
                      />
                      {editState.dryDoorIsOther && (
                        <input
                          type="text"
                          className="bdr-input"
                          placeholder="Custom dry door..."
                          value={editState.customDryDoorText}
                          onChange={(e) => updateField("customDryDoorText", e.target.value)}
                        />
                      )}
                    </div>
                  )}

                  {/* Threshold is Adjustable — independent of Adjusted
                      Threshold (D4). Post job form omnibus Phase 7. */}
                  <div className="jfrm-field">
                    <label className="bdr-field-label">Threshold is Adjustable</label>
                    <div className="jfrm-toggle-row">
                      <span style={{ fontSize: 13, color: editState.thresholdIsAdjustable ? "#aaa" : "#fff" }}>No</span>
                      <Switch
                        checked={editState.thresholdIsAdjustable === true}
                        onChange={(e) => updateField("thresholdIsAdjustable", e.target.checked)}
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": { color: "#2ecc71" },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                            backgroundColor: "#2ecc71",
                          },
                        }}
                      />
                      <span style={{ fontSize: 13, color: editState.thresholdIsAdjustable ? "#fff" : "#aaa" }}>Yes</span>
                    </div>
                  </div>

                  {/* Adjusted Threshold */}
                  <div className="jfrm-field">
                    <label className="bdr-field-label">Adjusted Threshold</label>
                    <div className="jfrm-toggle-row">
                      <span style={{ fontSize: 13, color: editState.adjustedThreshold ? "#aaa" : "#fff" }}>No</span>
                      <Switch
                        checked={editState.adjustedThreshold === true}
                        onChange={(e) => updateField("adjustedThreshold", e.target.checked)}
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": { color: "#2ecc71" },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#2ecc71" },
                        }}
                      />
                      <span style={{ fontSize: 13, color: editState.adjustedThreshold ? "#fff" : "#aaa" }}>Yes</span>
                    </div>
                  </div>

                  {/* Door Sweep */}
                  <div className="jfrm-field">
                    <label className="bdr-field-label">Door Sweep Replaced</label>
                    <div className="jfrm-toggle-row">
                      <span style={{ fontSize: 13, color: editState.replacedDoorSweep ? "#aaa" : "#fff" }}>No</span>
                      <Switch
                        checked={editState.replacedDoorSweep === true}
                        onChange={(e) => updateField("replacedDoorSweep", e.target.checked)}
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": { color: "#2ecc71" },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#2ecc71" },
                        }}
                      />
                      <span style={{ fontSize: 13, color: editState.replacedDoorSweep ? "#fff" : "#aaa" }}>Yes</span>
                    </div>
                  </div>

                  {/* Changed Weather Strip — 2026-07-22 parity with
                      JobFormReviewPage (previously fetched but not shown). */}
                  <div className="jfrm-field">
                    <label className="bdr-field-label">Changed Weather Strip</label>
                    <div className="jfrm-toggle-row">
                      <span style={{ fontSize: 13, color: editState.changedWeatherStrip ? "#aaa" : "#fff" }}>No</span>
                      <Switch
                        checked={editState.changedWeatherStrip === true}
                        onChange={(e) => updateField("changedWeatherStrip", e.target.checked)}
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": { color: "#2ecc71" },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#2ecc71" },
                        }}
                      />
                      <span style={{ fontSize: 13, color: editState.changedWeatherStrip ? "#fff" : "#aaa" }}>Yes</span>
                    </div>
                  </div>

                  {/* Foam Wedges — optional digits-only count; empty = not
                      provided (skipped in the approval customer note). */}
                  <div className="jfrm-field">
                    <label className="bdr-field-label">Foam Wedges</label>
                    <input
                      type="text"
                      className="bdr-input"
                      inputMode="numeric"
                      placeholder="Optional"
                      value={editState.foamWedges || ""}
                      onChange={(e) =>
                        updateField("foamWedges", e.target.value.replace(/\D/g, ""))
                      }
                    />
                  </div>

                  {/* Installed New Hardware */}
                  <div className="jfrm-field">
                    <label className="bdr-field-label">Installed New Hardware</label>
                    <div className="jfrm-toggle-row">
                      <span style={{ fontSize: 13, color: editState.installedNewHardware ? "#aaa" : "#fff" }}>No</span>
                      <Switch
                        checked={editState.installedNewHardware === true}
                        onChange={(e) => updateField("installedNewHardware", e.target.checked)}
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": { color: "#2ecc71" },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#2ecc71" },
                        }}
                      />
                      <span style={{ fontSize: 13, color: editState.installedNewHardware ? "#fff" : "#aaa" }}>Yes</span>
                    </div>
                  </div>

                  {/* Handle Operation */}
                  <div className="jfrm-field">
                    <label className="bdr-field-label">Handle Operation</label>
                    <BdrSelectPopup
                      id="jfrm-handle-operation"
                      className="bdr-input bdr-select-trigger"
                      label="Handle operation"
                      value={editState.handleOperationId}
                      onChange={(e) => updateField("handleOperationId", e.target.value)}
                      placeholder="-- Select --"
                      options={(hwOpData?.getAllHardwareOperationOptions || [])
                        .filter((opt) => opt?.optionName)
                        .map((opt) => ({ value: opt._id, label: opt.optionName }))}
                    />
                  </div>

                  {/* Deadbolt Operation */}
                  <div className="jfrm-field">
                    <label className="bdr-field-label">Deadbolt Operation</label>
                    <BdrSelectPopup
                      id="jfrm-deadbolt-operation"
                      className="bdr-input bdr-select-trigger"
                      label="Deadbolt operation"
                      value={editState.deadboltOperationId}
                      onChange={(e) => updateField("deadboltOperationId", e.target.value)}
                      placeholder="-- Select --"
                      options={(hwOpData?.getAllHardwareOperationOptions || [])
                        .filter((opt) => opt?.optionName)
                        .map((opt) => ({ value: opt._id, label: opt.optionName }))}
                    />
                  </div>

                  {/* Customer Satisfaction */}
                  {editState.customerSatisfaction && (
                    <div className="jfrm-field">
                      <label className="bdr-field-label">Customer Satisfaction</label>
                      <span className="jfrm-readonly-value">{editState.customerSatisfaction}</span>
                    </div>
                  )}

                  {/* Amount Received — stored as raw cents; normalize then
                      format for display (was previously shown raw). */}
                  {editState.amountReceived && (
                    <div className="jfrm-field">
                      <label className="bdr-field-label">Amount Received ({editState.paymentMethod || "Cash/Check"})</label>
                      <span className="jfrm-readonly-value">
                        {priceStrFormatter(
                          removePriceStrFormatter(editState.amountReceived)
                        )}
                      </span>
                    </div>
                  )}

                  {/* Tip display — parity with JobFormReviewPage. Calculated
                      client-side as amountReceived − invoiceTotal; the server
                      converts it to a "Tip" line item on approval. */}
                  {(() => {
                    const receivedCents =
                      parseInt(removePriceStrFormatter(editState.amountReceived || "0")) || 0;
                    const invoiceCents = parseInt(invoiceData?.invoiceTotal || "0") || 0;
                    const tipCents = receivedCents - invoiceCents;
                    if (receivedCents === 0 || !invoiceData) return null;
                    if (tipCents > 0) {
                      return (
                        <div className="jfrm-field">
                          <label className="bdr-field-label">Calculated Tip</label>
                          <span style={{ color: "#2ecc71", fontSize: 14, fontWeight: 600 }}>
                            {priceStrFormatter(tipCents.toString())}
                          </span>
                        </div>
                      );
                    } else if (tipCents < 0) {
                      return (
                        <div className="jfrm-field">
                          <label className="bdr-field-label">Underpayment</label>
                          <span style={{ color: "#e74c3c", fontSize: 14, fontWeight: 600 }}>
                            -{priceStrFormatter(Math.abs(tipCents).toString())}
                          </span>
                        </div>
                      );
                    } else {
                      const tipItem = invoiceData?.invoiceItems?.find(
                        (item) => item.itemName === "Tip"
                      );
                      return (
                        <div className="jfrm-field">
                          <label className="bdr-field-label">Tip</label>
                          <span style={{ color: tipItem ? "#2ecc71" : "#b0b0cc", fontSize: 14, fontWeight: tipItem ? 600 : 400 }}>
                            {tipItem
                              ? `${priceStrFormatter(tipItem.price)} (On Invoice)`
                              : "No tip"}
                          </span>
                        </div>
                      );
                    }
                  })()}
                </div>
              </div>

              {/* Push 1 build 146 — Referral Source section.
                  Save-on-submit: changes here only persist when the user
                  clicks Save Changes or Approve. Approval gate requires
                  this be set (either selected option OR custom text). */}
              <div className="jfrm-section">
                <div
                  className="jfrm-section-title"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  <span>Referral Source</span>
                  <CustomerNotesHelperButton clientId={form?.clientId} />
                </div>
                <div className="jfrm-fields-grid">
                  <div className="jfrm-field">
                    <label className="bdr-field-label">Source</label>
                    <BdrSelectPopup
                      id="jfrm-referred-by-source"
                      className="bdr-input bdr-select-trigger"
                      label="Source"
                      placeholder="— select source —"
                      options={[
                        ...referredByOptions.map((opt) => ({
                          value: opt._id,
                          label: opt.source,
                        })),
                        { value: "__other__", label: "Other (type below)" },
                      ]}
                      value={
                        editState.referredByIsOther
                          ? "__other__"
                          : editState.referredBySourceOptionId || ""
                      }
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === "__other__") {
                          // Show the custom input. We do NOT clear an
                          // existing typed value — if user toggles back
                          // and forth they keep their text.
                          updateField("referredByIsOther", true);
                          updateField("referredBySourceOptionId", "");
                        } else if (val === "") {
                          updateField("referredByIsOther", false);
                          updateField("referredBySourceOptionId", "");
                          updateField("referredBySource", "");
                        } else {
                          const selected = referredByOptions.find(
                            (o) => o._id === val
                          );
                          updateField("referredByIsOther", false);
                          updateField("referredBySourceOptionId", val);
                          updateField(
                            "referredBySource",
                            selected?.source || ""
                          );
                        }
                      }}
                    />
                  </div>
                  {editState.referredByIsOther && (
                    <div className="jfrm-field">
                      <label className="bdr-field-label">Custom Source</label>
                      <input
                        type="text"
                        className="bdr-input"
                        value={editState.referredBySource}
                        maxLength={120}
                        onChange={(e) =>
                          updateField("referredBySource", e.target.value)
                        }
                        placeholder="e.g. Word of mouth"
                      />
                    </div>
                  )}
                  {/* Phase 5: Referred By User dropdown. See
                      REFERRAL_SYSTEM_PLAN.md §6 Phase 5. Once set, the
                      dropdown locks (server enforces Q1 via
                      applyReferredByGuards). */}
                  <div className="jfrm-field">
                    <label className="bdr-field-label">
                      Referred By User (optional)
                      {editState.referredByUserIdLockedByServer && (
                        <span style={{ marginLeft: 8, fontSize: 11, color: "#b0b0cc", fontStyle: "italic" }}>
                          — locked
                        </span>
                      )}
                    </label>
                    <BdrSelectPopup
                      id="jfrm-referred-by-user"
                      className="bdr-input bdr-select-trigger"
                      label="Referred By User"
                      value={editState.referredByUserId || ""}
                      disabled={editState.referredByUserIdLockedByServer}
                      title={
                        editState.referredByUserIdLockedByServer
                          ? "Already referred by a BDR user — cannot change"
                          : "Pick a BDR user who referred this customer"
                      }
                      onChange={(e) =>
                        updateField("referredByUserId", e.target.value)
                      }
                      placeholder="— None —"
                      options={activeUsersForReferral.map((u) => ({
                        value: u._id,
                        label: `${u.firstName} ${u.lastName}`,
                      }))}
                    />
                    {isDeveloper &&
                      editState.referredByUserIdLockedByServer && (
                        <button
                          type="button"
                          className="jfrm-dev-clear-referral-btn"
                          onClick={handleDevClearReferral}
                          disabled={devClearing}
                          title="Developer-only: clears the refinisher user credit + linked payout for this job. Customer's referral source category is preserved."
                        >
                          {devClearing
                            ? "Clearing…"
                            : "🛠 Dev: Clear refinisher user"}
                        </button>
                      )}
                  </div>
                </div>
              </div>

              {/* Push 1 build 146 — Call For Payment banner + portal launch.
                  Banner is unmissable so the office knows this form is
                  blocked from approval until the card is run. The button
                  opens the existing admin payment portal in a new tab
                  with the invoice pre-loaded. saveTransaction success
                  in that tab fires the job_form_payment_completed SSE
                  which this modal listens for to refetch + unblock. */}
              {isCallForPayment && (
                <div className="jfrm-section jfrm-cfp-banner">
                  <div className="jfrm-cfp-title">
                    📞 Call Customer for Payment
                  </div>
                  <div className="jfrm-cfp-body">
                    The refinisher submitted this form with payment method
                    set to <strong>"Call For Payment"</strong>. Approval is
                    blocked until the card is run via the payment portal.
                    {form?.invoiceId && (
                      <div className="jfrm-cfp-actions">
                        <a
                          href={`/bdr-office/payments/process-payment/payment-id/${form.invoiceId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="jfrm-btn jfrm-btn-cfp-portal"
                        >
                          Open Payment Portal in New Tab
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Refinishers */}
              <div className="jfrm-section">
                <div className="jfrm-section-title">Refinishers</div>
                <div className="jfrm-refinisher-list">
                  {form.selectedRefinisherUsers?.map((user) => (
                    <div key={user._id} className="jfrm-refinisher-chip">
                      <span className="jfrm-refinisher-dot" style={{ backgroundColor: user.defaultColor || "#3a7bd5" }} />
                      {user.firstName} {user.lastName}
                    </div>
                  ))}
                  {form.customRefinisherNames?.filter((n) => n?.trim()).map((name, i) => (
                    <div key={`custom-${i}`} className="jfrm-refinisher-chip">
                      <span className="jfrm-refinisher-dot" style={{ backgroundColor: "#95a5a6" }} />
                      {name}
                    </div>
                  ))}
                </div>
              </div>

              {/* Notes */}
              <div className="jfrm-section">
                <div className="jfrm-section-title">Notes</div>
                {/* BdrInput, not a bare <textarea>: `autoGrow` is on by
                    default so the box fits its content instead of scrolling
                    inside itself. A fixed-height textarea inside a scrolling
                    modal gives two nested scrollbars and hides text that had
                    room to be shown. */}
                <BdrInput
                  multiline
                  rows={3}
                  value={editState.notes}
                  maxLength={1000}
                  onChange={(e) => updateField("notes", e.target.value)}
                  placeholder="Job notes..."
                />
              </div>

              {/* BA Groups */}
              {form.bagroupIds && form.bagroupIds.length > 0 && (
                <div className="jfrm-section">
                  <div className="jfrm-section-title">
                    Before / After Photos ({form.bagroupIds.length})
                  </div>
                  {form.bagroupIds.map((group) => (
                    <div key={group._id} className="jfrm-ba-group">
                      <button
                        className="jfrm-gallery-toggle"
                        onClick={() => toggleGallery(group._id)}
                      >
                        {galleryMode[group._id] ? "Side by Side" : "Week Gallery"}
                      </button>
                      <div className={`jfrm-ba-pair ${galleryMode[group._id] ? "gallery" : ""}`}>
                        <div className="jfrm-ba-img-wrapper">
                          <span className="jfrm-ba-label">Before</span>
                          {group.beforeId?._id ? (
                            <img
                              className="jfrm-ba-img"
                              src={photoFull(group.beforeId)}
                              alt="Before"
                              onClick={() => setModalImg(photoFull(group.beforeId))}
                            />
                          ) : (
                            <span className="jfrm-no-img">No image</span>
                          )}
                        </div>
                        <div className="jfrm-ba-img-wrapper">
                          <span className="jfrm-ba-label">After</span>
                          {group.afterId?._id ? (
                            <img
                              className="jfrm-ba-img"
                              src={photoFull(group.afterId)}
                              alt="After"
                              onClick={() => setModalImg(photoFull(group.afterId))}
                            />
                          ) : (
                            <span className="jfrm-no-img">No image</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Additional Photos */}
              {form.additionalPhotos && form.additionalPhotos.length > 0 && (
                <div className="jfrm-section">
                  <div className="jfrm-section-title">
                    Additional Photos ({form.additionalPhotos.length})
                  </div>
                  <div className="jfrm-additional-photos">
                    {form.additionalPhotos.map((photo) => (
                      <img
                        key={photo._id}
                        className="jfrm-additional-thumb"
                        src={photoThumb(photo)}
                        alt="Additional"
                        onClick={() => setModalImg(photoFull(photo))}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Status History */}
              <div className="jfrm-section">
                <div className="jfrm-section-title">Status History</div>
                <div className="jfrm-history">
                  {form.createdBy && (
                    <div className="jfrm-history-item">
                      <span className="jfrm-history-label">Created</span>
                      <span>{form.createdBy} &mdash; {formatDate(form.dateCreated)}</span>
                    </div>
                  )}
                  {form.submittedBy && (
                    <div className="jfrm-history-item">
                      <span className="jfrm-history-label">Submitted</span>
                      <span>{form.submittedBy} &mdash; {formatDate(form.dateSubmitted)}</span>
                    </div>
                  )}
                  {form.reviewedBy && (
                    <div className="jfrm-history-item">
                      <span className="jfrm-history-label">Reviewed</span>
                      <span>{form.reviewedBy} &mdash; {formatDate(form.dateReviewed)}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="jfrm-actions">
                {/* Surface rule: this panel is the saturated --bdr-grad-panel,
                    so the two committing actions take SOLID weights. Save is
                    the subdued one because Approve does everything Save does
                    and then approves — the action-plus-automation is the
                    primary. Request Changes is `action`, not `confirm`: it
                    writes nothing new, it hands the form back. */}
                <BdrButton onClick={handleSave} disabled={isMutating}>
                  {saving ? "Saving..." : "Save Changes"}
                </BdrButton>
                <BdrButton
                  variant="confirm-solid"
                  onClick={handleApprove}
                  disabled={isMutating}
                >
                  {statusUpdating ? "Updating..." : "Approve"}
                </BdrButton>
                <BdrButton
                  variant="action-solid"
                  onClick={handleRequestChanges}
                  disabled={isMutating}
                >
                  Request Changes
                </BdrButton>
              </div>
            </div>
          )}
        </div>
      </BdrModal>

      {/* Image lightbox */}
      <BdrModal
        open={Boolean(modalImg)}
        onClose={() => setModalImg(null)}
        surface="#111"
        maxWidth="none"
        showHeader={false}
        ariaLabel="Full size photo"
        panelClassName="bdr-lightbox"
        /* top: opened from this modal, which is itself raised */
        layer="top"
      >
        {modalImg && (
          <img
            src={modalImg}
            alt="Full size"
            onClick={() => setModalImg(null)}
          />
        )}
      </BdrModal>
    </>
  );
};

export default JobFormReviewModal;
